import { useState, useRef, useEffect } from "react";
import type { Project } from "@/mocks/projects";

interface ProjectGalleryProps {
  project: Project;
}

const isVideo = (src: string) => src.toLowerCase().endsWith(".mp4");

export default function ProjectGallery({ project }: ProjectGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (lightboxIndex !== null && videoRef.current) {
      videoRef.current.load();
    }
  }, [lightboxIndex]);

  if (!project.gallery || project.gallery.length === 0) return null;

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () =>
    setLightboxIndex((p) =>
      p !== null ? (p - 1 + project.gallery!.length) % project.gallery!.length : 0
    );
  const next = () =>
    setLightboxIndex((p) =>
      p !== null ? (p + 1) % project.gallery!.length : 0
    );

  const currentSrc = lightboxIndex !== null ? project.gallery[lightboxIndex] : null;

  return (
    <section className="bg-[#F8F9FA] py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <span className="text-[#4CAF50] text-xs font-semibold uppercase tracking-widest mb-3 block">
          Fotoğraflar & Videolar
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B4A] mb-8">
          Proje Galerisi
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {project.gallery.map((src, index) => (
            <div
              key={index}
              className="relative h-56 rounded-xl overflow-hidden cursor-pointer group bg-[#1A2B4A]"
              onClick={() => openLightbox(index)}
            >
              {isVideo(src) ? (
                <>
                  <video
                    src={src}
                    className="w-full h-full object-cover opacity-80"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 backdrop-blur-sm transition-all duration-300">
                      <i className="ri-play-fill text-white text-2xl translate-x-0.5" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <img
                    src={src}
                    alt={`${project.title} - Fotoğraf ${index + 1}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#1A2B4A]/0 group-hover:bg-[#1A2B4A]/40 transition-all duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/0 group-hover:bg-white/90 transition-all duration-300 scale-75 group-hover:scale-100">
                      <i className="ri-zoom-in-line text-[#1A2B4A] text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && currentSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-xl" />
          </button>

          <button
            className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); prev(); }}
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>

          <div
            className="max-w-4xl w-full max-h-[80vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideo(currentSrc) ? (
              <video
                ref={videoRef}
                src={currentSrc}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg"
              />
            ) : (
              <img
                src={currentSrc}
                alt={`${project.title} - Fotoğraf ${lightboxIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            )}
          </div>

          <button
            className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); next(); }}
          >
            <i className="ri-arrow-right-line text-xl" />
          </button>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/50 text-sm">
            {lightboxIndex + 1} / {project.gallery.length}
          </div>
        </div>
      )}
    </section>
  );
}
